import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
} from '@mui/material';
import { jids } from '../../../../data/forms/generalDropdownOptions.js';
import { depotTimeWorked } from '../../../../data/forms/depot/depotDropdownOptions.js';
import {
  actionPerformedOptions,
  actionTypeOptionsMap,
} from '../../../../data/forms/depot/rltWorklogDropdownOptions.js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';

const RltWorklogForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    actionPerformed: '',
    actionType: '',
    date: null,
    totalWorkTime: '',
    notes: '',
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'actionPerformed') {
      setForm(prevForm => ({
        ...prevForm,
        actionType: '',
      }));
    }
  };

  const handleDateChange = value => {
    setForm({
      ...form,
      date: value,
    });
  };

  const handleClear = () => {
    setForm(prevForm => ({
      ...prevForm,
      jurisdiction: '',
      actionPerformed: '',
      actionType: '',
      date: null,
      totalWorkTime: '',
      notes: '',
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/rlt/submitLog`,
        data: {
          ...form,
          date: form.date ? form.date.format('YYYY-MM-DD') : null,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const { jurisdiction, actionPerformed, actionType, date, totalWorkTime } = form;

    if (!jurisdiction || !actionPerformed || !date || !totalWorkTime) {
      return false;
    }

    if (
      actionPerformed &&
      actionPerformed !== 'Special Event Prep' &&
      actionPerformed !== 'Service Calls' &&
      !actionType
    ) {
      return false;
    }

    return true;
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        display: 'flex',
        justifyItems: 'center',
        width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
      }}
    >
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl
          required
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        >
          <InputLabel>{translations.jurisdiction || 'Jurisdiction'}</InputLabel>
          <Select
            value={form.jurisdiction}
            label={translations.jurisdiction || 'Jurisdiction'}
            onChange={handleChange}
            name='jurisdiction'
          >
            {jids.map(jid => (
              <MenuItem value={jid}>{jid}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          required
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        >
          <InputLabel>{'Action Performed'}</InputLabel>
          <Select
            value={form.actionPerformed}
            label={'Action Performed'}
            onChange={handleChange}
            name='actionPerformed'
          >
            {actionPerformedOptions.map(option => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={'Date*'}
            value={form.date}
            onChange={value => handleDateChange(value)}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </LocalizationProvider>
        <FormControl
          required
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        >
          <InputLabel>{'Total Work Time (minutes)'}</InputLabel>
          <Select
            value={form.totalWorkTime}
            label={'Total Work Time (minutes)'}
            onChange={handleChange}
            name='totalWorkTime'
          >
            {depotTimeWorked.map(option => (
              <MenuItem value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label='Name'
          disabled
          value={`${user.firstName} ${user.lastName}`}
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        />
        <Tooltip
          title={
            form.actionPerformed === 'Special Event Prep' ||
            form.actionPerformed === 'Service Calls'
              ? `Action Type is not required for ${form.actionPerformed}`
              : form.actionPerformed === ''
              ? 'Please select Action Performed to enable this field'
              : ''
          }
          arrow
        >
          <FormControl
            required={
              form.actionPerformed !== 'Special Event Prep' &&
              form.actionPerformed !== 'Service Calls' &&
              form.actionPerformed !== ''
            }
            disabled={
              !form.actionPerformed ||
              form.actionPerformed === 'Special Event Prep' ||
              form.actionPerformed === 'Service Calls'
            }
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Action Type'}</InputLabel>
            <Select
              value={form.actionType}
              label={'Action Type'}
              onChange={handleChange}
              name='actionType'
            >
              {actionTypeOptionsMap[form.actionPerformed]?.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
        <TextField
          label='Notes (Optional)'
          name='notes'
          value={form.notes}
          onChange={handleChange}
          multiline
          rows={4.1}
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        />
      </Grid>
      <Grid
        size={12}
        sx={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant='contained'
          sx={{
            backgroundColor: 'var(--aldebaran-yellow)',
            color: '#000000',
            fontFamily: 'BrightstarSans-Regular',
            width: '10%',
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
        <Tooltip
          title={
            !isFormValid() ? 'Please fill in all required fields to enable the Submit button' : ''
          }
          arrow
        >
          <Box>
            <Button
              disabled={!isFormValid() || isSubmitting}
              variant='contained'
              sx={{
                backgroundColor: 'var(--vega-blue)',
                fontFamily: 'BrightstarSans-Regular',
                width: '100%',
              }}
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              {'Submit'}
            </Button>
          </Box>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default RltWorklogForm;
